import { BuildTableData } from '../types';
import { FormattedMessage } from 'react-intl';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { sharedMessages } from '../../sharedIntlLabels';
import { SpecificityIndicator } from '../../Indicators';
import { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import * as React from 'react';
import urls from '../../../../../src/util/urls';

// #region Value getters

export const getCKB = (value: any) => {
  const { jaxKnowledge } = value;

  if (!jaxKnowledge) {
    return null;
  }

  return <SpecificityIndicator variant="copynumber" match={jaxKnowledge} />;
};

export const getGene = (value: any) => {
  const { geneId } = value;

  if (!geneId) {
    return null;
  }

  return (
    <Link to={urls.ncbi.gene(geneId)} onClick={e => e.stopPropagation()} newTab>
      {geneId}
    </Link>
  );
};

export const getGeneTextOnly = (value: any) => value?.geneId;

export const getEffect = (value: any) => value?.status;

export const getPosition = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  build, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const { chromosome, endPosition, startPosition } = value;

  if (!(startPosition && endPosition)) {
    return null;
  }

  if (!build && chromosome) {
    return (
      <FormattedMessage
        id="omicsExplorer.structuralVariantsContent.table.position.noBuildValue"
        defaultMessage="{chromosome}:{startPosition}-{endPosition}"
        values={{
          chromosome,
          startPosition,
          endPosition,
        }}
      />
    );
  }

  if (!build || !chromosome) {
    return (
      <FormattedMessage
        id="omicsExplorer.structuralVariantsContent.table.position.noBuildNoChromosomeValue"
        defaultMessage="{startPosition}-{endPosition}"
        values={{
          startPosition,
          endPosition,
        }}
      />
    );
  }

  return (
    <Link
      to={urls.ucsc.genome(
        build,
        chromosome,
        `${startPosition}-${endPosition}`,
      )}
      onClick={e => e.stopPropagation()}
      newTab
    >
      <FormattedMessage
        id="omicsExplorer.structuralVariantsContent.table.position.value"
        defaultMessage="{chromosome}:{startPosition}-{endPosition}"
        values={{
          chromosome,
          startPosition,
          endPosition,
        }}
      />
    </Link>
  );
};

export const getCopyNumber = (value: any) => value?.copyNumber;

export const getLabReportedSignificance = (value: any) => value?.interpretation;

// #endregion

export const buildTableData = ({
  activeSequence,
  build,
  formatMessage,
  fetchPrependedColumns,
  onSort,
  variantSet,
}: BuildTableData): Array<TableConfiguration> => [
  ...(fetchPrependedColumns?.(variantSet, activeSequence) ?? []),
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.jax),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['jaxKnowledge'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getCKB(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.gene),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['geneId'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getGene(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.effect),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['status'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getEffect(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.position),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['chromosome' || 25, 'startPosition'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection, sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getPosition({ build, value }),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.copyNumber),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['copyNumber'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getCopyNumber(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.labReportedSignificance),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['interpretation'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getLabReportedSignificance(value),
    },
  },
];
